import { useEffect, type Ref } from 'react'
import { skipToken, useQuery } from '@tanstack/react-query'
import { MessageSquare } from 'lucide-react'

import { assistantEffects } from '@/lib/assistant-effects.generated'
import { focusMain } from '@/lib/use-route-focus'
import { useSelectedEntityId } from '@/lib/use-selected-entity'
import { useAnnounce } from '@/shared/providers/announcer-provider'
import { Button } from '@/ui/button'

import { getAssistantAvailability } from '../../api/assistant-api'
import { assistantAvailabilityKeys } from '../../api/query-keys'

export function AssistantToggle({
  open,
  onToggle,
  ref,
}: {
  open: boolean
  onToggle: (open: boolean) => void
  ref: Ref<HTMLButtonElement>
}) {
  const entityId = useSelectedEntityId()
  const announce = useAnnounce()
  const availability = useQuery({
    queryKey: assistantAvailabilityKeys.entity(entityId ?? ''),
    queryFn: entityId ? () => getAssistantAvailability(entityId) : skipToken,
    meta: { assistantEffects: [assistantEffects.settingsChanged] },
  })
  const knownUnavailable = availability.data?.available === false

  useEffect(() => {
    if (!knownUnavailable || !open) return
    const focusWillBeLost =
      document.activeElement === document.body
      || document.activeElement?.closest('[role="dialog"][aria-label="AI assistant"]') != null
    onToggle(false)
    announce('AI assistant closed. It is turned off for this entity.')
    if (focusWillBeLost) focusMain()
  }, [knownUnavailable, open, onToggle, announce])

  if (knownUnavailable) return null

  return (
    <Button
      ref={ref}
      type="button"
      variant="chrome"
      size="icon-lg"
      aria-label={open ? 'Close AI assistant' : 'Open AI assistant'}
      aria-expanded={open}
      onClick={() => onToggle(!open)}
    >
      <MessageSquare className="size-4" />
    </Button>
  )
}
